import { create } from 'zustand'
import { api } from '../lib/api'
import { getSocket } from '../lib/socket'

export interface Notification {
  id: string
  type: string
  title: string
  message: string
  isRead: boolean
  metadata: Record<string, unknown> | null
  createdAt: string
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  fetch: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  addNotification: (n: Notification) => void
  subscribeSocket: () => void
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,

  fetch: async () => {
    try {
      const res = await api.get<{ data: { notifications: Notification[]; unreadCount: number } }>(
        '/notifications',
      )
      set({ notifications: res.data.data.notifications, unreadCount: res.data.data.unreadCount })
    } catch {
      // Silent fail
    }
  },

  markRead: async (id) => {
    await api.patch(`/notifications/${id}/read`)
    set((s) => ({
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      unreadCount: Math.max(0, s.unreadCount - 1),
    }))
  },

  markAllRead: async () => {
    await api.patch('/notifications/read-all')
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }))
  },

  addNotification: (n) =>
    set((s) => ({
      notifications: [n, ...s.notifications].slice(0, 50),
      unreadCount: s.unreadCount + 1,
    })),

  subscribeSocket: () => {
    const socket = getSocket()
    if (!socket) return

    socket.on('notification:new', (n: Notification) => {
      get().addNotification(n)
    })
    socket.on('notification:broadcast', (n: { title: string; message: string }) => {
      get().addNotification({ id: Date.now().toString(), type: 'SYSTEM', isRead: false, metadata: null, createdAt: new Date().toISOString(), ...n })
    })
  },
}))
